import { initNewsAccordion, resetNewsAccordionHashScroll } from "./news-accordion";
import { initNewsYears } from "./news-years";
import { initLazyMedia } from "../utils/lazy-media";

const SCROLL_ROOT_MARGIN = "320px";
let scrollObserver = null;

function getSentinel(container) {
  return container.querySelector(".js-news-load-more-sentinel");
}

function updateContainerState(container, { nextOffset, hasMore }) {
  container.dataset.offset = String(nextOffset);
  container.dataset.hasMore = hasMore ? "true" : "false";

  const sentinel = getSentinel(container);
  if (sentinel) {
    sentinel.hidden = !hasMore;
  }
}

function buildRequestUrl(container) {
  const url = new URL(container.dataset.loadUrl, window.location.origin);
  const batchSize = Number(container.dataset.batchSize) || 15;
  const offset = Number(container.dataset.offset) || batchSize;

  url.searchParams.set("offset", String(offset));
  url.searchParams.set("limit", String(batchSize));

  return url;
}

async function fetchBatch(container) {
  const response = await fetch(buildRequestUrl(container), {
    headers: {
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to load news (${response.status})`);
  }

  return response.json();
}

function appendBatch(container, html) {
  const sentinel = getSentinel(container);
  const template = document.createElement("template");
  template.innerHTML = html.trim();
  const fragment = template.content;

  if (sentinel) {
    sentinel.before(fragment);
  } else {
    container.appendChild(fragment);
  }
}

async function loadMore(container) {
  if (container.dataset.hasMore !== "true" || container.dataset.loading === "true") {
    return false;
  }

  container.dataset.loading = "true";

  try {
    const data = await fetchBatch(container);

    if (data.html) {
      appendBatch(container, data.html);
      initLazyMedia(container);
      // Rebind clicks only — do not re-run hash scroll-to.
      initNewsAccordion({ handleHash: false });
      initNewsYears();
    }

    updateContainerState(container, {
      nextOffset: data.nextOffset,
      hasMore: data.hasMore,
    });

    return true;
  } catch (error) {
    console.error(error);
    return false;
  } finally {
    container.dataset.loading = "false";
  }
}

async function ensureHashArticle(container) {
  const hash = window.location.hash.replace(/^#/, "");

  if (!hash || document.getElementById(hash)) {
    return;
  }

  while (container.dataset.hasMore === "true") {
    const loaded = await loadMore(container);

    if (!loaded || document.getElementById(hash)) {
      break;
    }
  }

  if (document.getElementById(hash)) {
    initNewsAccordion({ handleHash: true });
    initNewsYears();
  }
}

let loadMoreAbort = null;

export function initNewsLoadMore() {
  const container = document.querySelector(".js-news-articles");

  if (!container) {
    return;
  }

  loadMoreAbort?.abort();
  loadMoreAbort = new AbortController();

  const sentinel = getSentinel(container);

  if (sentinel && "IntersectionObserver" in window) {
    if (scrollObserver) {
      scrollObserver.disconnect();
    }

    scrollObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }

        loadMore(container);
      },
      { rootMargin: SCROLL_ROOT_MARGIN }
    );

    scrollObserver.observe(sentinel);
  }

  ensureHashArticle(container);
}

export function destroyNewsLoadMore() {
  loadMoreAbort?.abort();
  loadMoreAbort = null;
  resetNewsAccordionHashScroll();

  if (scrollObserver) {
    scrollObserver.disconnect();
    scrollObserver = null;
  }
}
